import { createTheme, outlinedInputClasses } from "@mui/material";
import { frFR } from "@mui/material/locale";
import { MuiAlert } from "./components/Alert";
import { MuiAutocomplete } from "./components/Autocomplete";
import { MuiButton } from "./components/Button";
import { MuiCheckbox } from "./components/Checkbox";
import { palette } from "./palette";
import { typography } from "./typography";
declare module "@mui/material/styles" {
  interface Theme {
    readonly navbarSize: string;
    readonly sidebarSize: string;
    readonly searchHeight: string;
  }
  interface ThemeOptions {
    readonly navbarSize?: string;
    readonly sidebarSize?: string;
    readonly searchHeight?: string;
  }
}

declare module "@mui/material/Badge" {
  interface BadgePropsColorOverrides {
    yellow: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsSizeOverrides {
    xs: true;
  }
}

declare module "@mui/material/Fab" {
  interface FabPropsColorOverrides {
    white: true;
    main: true;
  }
  interface FabPropsSizeOverrides {
    xs: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    blue: true;
    yellow: true;
    paused: true;
    missed: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    yellow: true;
    blue: true;
    danger: true;
  }
}
declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    tablet: true;
    desktop: true;
  }
}
export const themeBBJ = createTheme(
  {
    navbarSize: "48px",
    sidebarSize: "56px",
    searchHeight: "65px",
    spacing: 8,
    palette: palette,
    typography: typography,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        desktop: 1024,
        tablet: 768,
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            borderRadius: "0px",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: "Source Sans Pro",
            fontSize: "14px",
            borderRadius: "0px",
            [`& .${outlinedInputClasses.notchedOutline} > legend`]: {
              fontFamily: "Source Sans Pro",
              /** 0.75 is the scaling value used on the label */
              fontSize: "calc(14px * 0.75)",
            },
          },
        },
      },
      MuiButton,
      MuiCheckbox,
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "0px",
            backgroundColor: "white",
          },
          adornedStart: {
            paddingLeft: "6px",
          },
          adornedEnd: {
            paddingRight: "6px",
          },
          notchedOutline: {
            fontSize: "13px",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: "8px",
          },
          paperFullScreen: {
            borderRadius: "0px",
          },
          paperWidthXs: {
            maxWidth: "475px",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "24px",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          colorSecondary: {
            backgroundColor: palette.yellow.main,
            color: palette.primary.dark,
            "&&:hover": {
              backgroundColor: palette.yellow.dark,
              color: palette.primary.dark,
            },
          },
          deleteIconColorSecondary: {
            color: "black",
          },
          label: {
            fontFamily: "Source Sans Pro",
            fontWeight: 400,
            fontSize: "14px",
            letterSpacing: "0.15px",
          },
        },
      },
      MuiAlert: MuiAlert,
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: "0px",
            filter:
              "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.14)) drop-shadow(0px 3px 4px rgba(0, 0, 0, 0.12)) drop-shadow(0px 1px 5px rgba(0, 0, 0, 0.2))",
          },
        },
      },
      MuiIcon: {
        styleOverrides: {
          root: {
            overflow: "initial",
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          anchorOriginTopRightRectangular: {
            right: "4px",
          },
        },
      },
      MuiAutocomplete,
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            display: "flex",
            flexDirection: "column",
            padding: "8px",
            alignItems: "flex-start",
            gap: "0px",
            borderRadius: "4px",
            opacity: "0.9 !important",
            background: "#202020",
            boxShadow:
              "0px 5px 6px 0px rgba(0, 0, 0, 0.20), 0px 3px 16px 0px rgba(0, 0, 0, 0.12), 0px 9px 12px 0px rgba(0, 0, 0, 0.14)",
            fontFamily: "Source Sans Pro",
            fontSize: "14px",
            fontWeight: 400,
          },
          arrow: {
            color: "#202020",
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          separator: {
            marginLeft: "4px",
            marginRight: "4px",
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { color: "white" },
                style: {
                  backgroundColor: "white",
                  cursor: "pointer",
                  "&.Mui-disabled": {
                    background: "white",
                    boxShadow:
                      "0px 0px 2px rgba(0, 0, 0, 0.14), 0px 2px 2px rgba(0, 0, 0, 0.12), 0px 1px 3px rgba(0, 0, 0, 0.2);",
                  },
                },
              },
              {
                props: { color: "main" },
                style: {
                  backgroundColor: "white",
                  border: `2px solid ${palette.primary.main}`,
                },
              },
              {
                props: { size: "large" },
                style: {
                  width: 32,
                  height: 32,
                  minHeight: 32,
                  "& .MuiSvgIcon-root": {
                    fontSize: 20,
                  },
                },
              },
            ],
            width: 24,
            height: 24,
            minHeight: 24,
            boxShadow:
              "0px 0px 2px rgba(0, 0, 0, 0.14), 0px 2px 2px rgba(0, 0, 0, 0.12), 0px 1px 3px rgba(0, 0, 0, 0.2);",
            "& .MuiSvgIcon-root": {
              fontSize: 16,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            margin: 0,
          },
        },
      },
    },
  },
  frFR
);
